import React from 'react';
import { toast } from 'sonner';
import { uploadsApi } from '@/api/uploads';
import { Spinner } from '@/components/icons';
import { HoverIntentGroup } from '@/components/ui/hover-intent-group';
import { Progress } from '@/components/ui/progress';
import { extractApiErrorMessage } from '@/lib/api-errors';
import { ImageBlock, TestBlock } from '@/types/report-payload';
import { resolvePreviewUrl, setPreviewUrl } from '@/utils/preview-cache';
import { IMAGE_UPLOAD_ACCEPT, validateImageFile } from '@/utils/validate-image-file';
import PlaceholderBlock from '../wizard/test-blocks/PlaceholderBlock';
import { ChartResizeHandles } from './chart-resize/ChartResizeHandles';
import { useChartResize } from './chart-resize/useChartResize';
import { imageBoxStyle } from './image-box';
import { stripSplitBlockId } from './pagination/utils';
import { takePendingImageFile } from './pending-image-files';

/** Fallback box shape for an image whose natural size we never recorded. */
const FALLBACK_INTRINSIC = { width: 4, height: 3 };

/**
 * Read the natural size before uploading, so the block can reserve its height
 * from an aspect ratio rather than from the loaded image. Without it every
 * image would repaginate the report the moment it finishes loading.
 */
const readIntrinsicSize = (file: File): Promise<{ width: number; height: number } | null> =>
    new Promise((resolve) => {
        const objectUrl = URL.createObjectURL(file);
        const probe = new Image();

        probe.onload = () => {
            resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
            URL.revokeObjectURL(objectUrl);
        };
        probe.onerror = () => {
            resolve(null);
            URL.revokeObjectURL(objectUrl);
        };
        probe.src = objectUrl;
    });

interface TestImageProps {
    block: ImageBlock;
    disabled?: boolean;
    onBlockUpdate?: (blockId: string | number, updates: Partial<TestBlock>) => void;
    /** Rendered inside the hidden measurer purely to size the block. */
    isMeasuring?: boolean;
}

const TestImageComponent: React.FC<TestImageProps> = ({
    block,
    disabled = false,
    onBlockUpdate,
    isMeasuring = false,
}) => {
    const [progress, setProgress] = React.useState<number | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const intrinsic = block.intrinsic ?? FALLBACK_INTRINSIC;
    const handleCommit = React.useCallback(
        (pct: number) => onBlockUpdate?.(block.id, { size: { width_pct: pct } }),
        [block.id, onBlockUpdate],
    );
    const { livePct, isResizing, handleResizeStart, didDragRef } = useChartResize({
        disabled: disabled || !onBlockUpdate,
        containerRef,
        storedPct: block.size?.width_pct,
        aspect: intrinsic.width / intrinsic.height,
        onCommit: handleCommit,
    });

    const upload = React.useCallback(
        async (file: File) => {
            const validationError = validateImageFile(file);
            if (validationError) {
                toast.error(validationError);
                return;
            }

            setProgress(0);

            try {
                const measured = await readIntrinsicSize(file);
                const ref = await uploadsApi.uploadAndConfirm({
                    file,
                    intent: 'report_image',
                    onProgress: setProgress,
                });

                // Show the local file until the first save round-trip hands
                // back a signed URL for the stored (re-encoded) object.
                setPreviewUrl(ref, URL.createObjectURL(file));
                onBlockUpdate?.(block.id, {
                    ref,
                    has_content: true,
                    ...(measured ? { intrinsic: measured } : {}),
                } as Partial<TestBlock>);
            } catch (err) {
                console.error('Report image upload failed', err);
                // A toast, not inline state: replacing an existing image keeps
                // rendering the old one, so an inline message would never show.
                // The backend genuinely rejects corrupt or oversized images and
                // says which, in pt-BR.
                toast.error(extractApiErrorMessage(err, 'Falha no upload. Tente novamente.'));
            } finally {
                setProgress(null);
            }
        },
        [block.id, onBlockUpdate],
    );

    // A block created by dropping a file starts its upload on mount, so the
    // user is not asked to choose the same file again. The hidden measurer
    // mounts a second copy of every block — it must not claim the file.
    React.useEffect(() => {
        if (isMeasuring || block.ref) return;

        const dropped = takePendingImageFile(block.id);
        if (dropped) upload(dropped);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openPicker = () => {
        if (disabled) return;
        inputRef.current?.click();
    };

    const fileInput = (
        <input
            ref={inputRef}
            type="file"
            // How the tools panel reaches this picker: the sidebar cannot hold a
            // ref into the paginated preview, so it addresses the input by id.
            data-image-picker-for={stripSplitBlockId(String(block.id ?? ''))}
            accept={IMAGE_UPLOAD_ACCEPT}
            className="hidden"
            onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) upload(file);
            }}
        />
    );

    if (progress !== null) {
        return (
            <div className="mb-6 rounded-lg border-2 border-dashed border-border-strong bg-card p-6">
                <div className="flex items-center gap-3">
                    <Spinner className="size-4 animate-spin text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Enviando imagem…</p>
                </div>
                <Progress value={progress} className="mt-3" />
            </div>
        );
    }

    if (!block.ref) {
        return (
            <>
                {fileInput}
                <PlaceholderBlock
                    type="image"
                    label="Adicionar Imagem"
                    description="Clique para escolher uma imagem (JPG, PNG ou WebP)"
                    onClick={openPicker}
                    disabled={disabled}
                />
            </>
        );
    }

    const box = imageBoxStyle(livePct ?? block.size?.width_pct, intrinsic);
    // Undefined when the stored reference reached us unsigned. The box still
    // renders: its height comes from the stored aspect ratio and the hidden
    // measurer runs this same component, so dropping it would move the page
    // breaks.
    const src = resolvePreviewUrl(block.ref);

    return (
        <>
            {fileInput}
            <HoverIntentGroup
                ref={containerRef}
                hoverIntent={{ disabled, keepOpen: isResizing }}
                className={`group/media relative ${disabled ? '' : 'cursor-pointer'}`}
                style={box.style}
                onClick={() => {
                    if (didDragRef.current) {
                        didDragRef.current = false;
                        return;
                    }
                    openPicker();
                }}
            >
                <div className="pointer-events-none absolute inset-0 z-10 rounded-lg border-2 border-transparent group-data-[hover-intent=on]/media:border-blue-200" />
                {!disabled && !isResizing && (
                    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center opacity-0 transition-opacity duration-200 group-data-[hover-intent=on]/media:opacity-100">
                        <span className="flex items-center gap-1.5 rounded-full bg-slate-900/80 px-3 py-1.5 text-xs font-medium text-white shadow-sm">
                            Clique para trocar
                        </span>
                    </div>
                )}
                {!disabled && onBlockUpdate && (
                    <ChartResizeHandles
                        isResizing={isResizing}
                        livePct={livePct}
                        onResizeStart={handleResizeStart}
                    />
                )}
                {src ? (
                    <img
                        src={src}
                        alt=""
                        // The box owns the height (from the stored aspect
                        // ratio); the image must never size it, or pagination
                        // reflows the moment it loads.
                        className="size-full object-contain"
                    />
                ) : (
                    <div className="size-full rounded-lg bg-slate-100" />
                )}
            </HoverIntentGroup>
        </>
    );
};

const TestImage = React.memo(TestImageComponent, (prev, next) => {
    return (
        prev.block === next.block &&
        prev.disabled === next.disabled &&
        prev.isMeasuring === next.isMeasuring
    );
});

export default TestImage;
